import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, makeStyles, mergeClasses, Option, Text } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useCommonStyles } from '../../../common/hooks';
import { OverrideReason, overrideReasons } from '../../../models';
import { FormInput } from '../../shared-components/form/FormInput';
import { SelectOptions } from '../../shared-components/form/SelectOptions';

interface OverrideScrubbingModalProps {
  nominationTitle: string;
  onOverride(overrideJustification: string): Promise<void>;
  onCancel(): void;
}

const useStyles = makeStyles({
  modalSurface: {
    maxWidth: '416px',
    width: '100%',
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
});

const OverrideScrubbingModalImpl: React.FC<OverrideScrubbingModalProps> = (props) => {
  const {
    nominationTitle,
    onOverride,
    onCancel,
  } = props;

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const [reason, setReason] = useState<OverrideReason>();
  const [freeText, setFreeText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValid = !!reason && freeText.trim().length >= 5;

  const submit = async (): Promise<void> => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onOverride(`${reason}: ${freeText.trim()}`);
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    if (isSubmitting) return;
    onCancel();
  };

  return (
    <Dialog
      open
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DialogSurface className={mergeClasses(commonStyles.modalSurface, styles.modalSurface)}>
        <DialogBody className={commonStyles.modalBody}>
          <DialogTitle
            action={(
              <Button
                title='close'
                appearance='transparent'
                icon={<DismissRegular />}
                disabled={isSubmitting}
                onClick={() => handleCancel()}
              />
            )}
          >
            Override disqualification
          </DialogTitle>
          <DialogContent className={styles.modalContent}>
            <Text className='text--blue' weight='semibold' block>
              {nominationTitle}
            </Text>
            <SelectOptions
              label='Override reason'
              required
              onOptionSelect={(data) => setReason(data.optionValue as OverrideReason)}
              dropdownProps={{
                placeholder: 'Select a reason',
                value: reason ?? '',
                selectedOptions: reason ? [reason] : [],
              }}
            >
              {overrideReasons.map((overrideReason) => <Option key={overrideReason} value={overrideReason}>{overrideReason}</Option>)}
            </SelectOptions>
            <FormInput
              label='Justification'
              required
              onChange={(value) => setFreeText(value)}
              textAreaProps={{
                value: freeText,
                maxLength: 3900,
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button appearance='secondary' disabled={isSubmitting} onClick={() => handleCancel()}>
              Cancel
            </Button>
            <Button appearance='primary' disabled={!isValid || isSubmitting} onClick={() => submit()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const OverrideScrubbingModal = observer(OverrideScrubbingModalImpl);
